import React, { useState } from "react";
import { useLocalStore, useObserver } from "mobx-react";
import { makeStyles } from "@material-ui/core/styles";
import { Button, ButtonProps, Typography, Grid } from "@material-ui/core";
import { Form, FormxDebugger } from "@common/formx";
import { ConsultantQualityReferenceType } from "@graphql";
import {
  DatePicker,
  SingleSelect,
  FileUpload,
  useSnackbarsPipeline,
  AlertDialog
} from "@bonliva-crm/ui";
import { ErrorCard, Spinner, SpinnerOverlay, WithTooltip } from "@components";
import { useLocale, useTenant } from "@hooks";
import { ConsultantQualityItemEditorStore } from "./ConsultantQualityItemEditorStore";
import { StatusMeta } from "./Statuses/StatusMeta";

type Props = {
  consultantId: string;
  country: string;
  itemName: string;
  itemType: string;
  templateName: string;
  onClose?: () => any;
  ivoGrundSetOn?: Date;
  referenceId?: string;
  isNationalReference?: boolean;
};

export const ConsultantQualityItemEditor: React.FC<Props> = (props) => {
  const { onClose } = props;
  const snackbar = useSnackbarsPipeline();
  const [alertOpen, setAlertOpen] = useState(false);
  const { locale } = useLocale();
  const { tenant } = useTenant()

  const localStore = useLocalStore(
    () =>
      new ConsultantQualityItemEditorStore(
        props.consultantId,
        props.country,
        props.itemName,
        props.templateName,
        props.onClose,
        props.ivoGrundSetOn,
        props.referenceId,
        props.isNationalReference,
        snackbar,
        tenant
      )
  );

  const classes = useStyles({});

  return useObserver(() => {
    const { loading, data, referenceOpt, form } = localStore;

    let actionButtons: (ButtonProps & {
      label: string;
      value: string;
      tooltipTitle?: string;
    })[] = [];

    if (props.referenceId) {
      actionButtons = [
        {
          label: locale.qualityStatuses.NotApproved,
          // value: ConsultantQualityReferenceStatus.NotApproved,
          value: "NotApproved",
          // onClick: props.isNationalReference
          //   ? async () => {
          //       await localStore.setStatus(ConsultantQualityReferenceStatus.NotApproved);
          //       localStore.submitNationalProcurementReference();
          //     }
          //   : async () => {
          //       await localStore.setStatus(ConsultantQualityReferenceStatus.NotApproved);
          //       localStore.submitReference();
          //     },
          disabled: !form.valid
        },
        {
          label: locale.qualityStatuses.RequestNotAnswered,
          // value: ConsultantQualityReferenceStatus.RequestNotAnswered,
          value: "RequestNotAnswered",
          // onClick: props.isNationalReference
          //   ? async () => {
          //       await localStore.setStatus(
          //         ConsultantQualityReferenceStatus.RequestNotAnswered
          //       );
          //       localStore.submitNationalProcurementReference();
          //     }
          //   : async () => {
          //       await localStore.setStatus(
          //         ConsultantQualityReferenceStatus.RequestNotAnswered
          //       );
          //       localStore.submitReference();
          //     },
          disabled: !form.valid
        },
        {
          label: locale.qualityStatuses.ManuallyApproved,
          // value: ConsultantQualityReferenceStatus.ManuallyApproved,
          value: "ManuallyApproved",
          onClick: props.isNationalReference
            ? localStore.submitNationalProcurementReference
            : localStore.submitReference
          // disabled:
          //   !form.canSubmit ||
          //   localStore.checkStatus(ConsultantQualityReferenceStatus.ManuallyApproved)
          //     .disabled
        },
        {
          variant: "contained" as const,
          label: locale.qualityStatuses.Approved,
          // value: ConsultantQualityReferenceStatus.Approved,
          value: "Approved",
          onClick: props.isNationalReference
            ? localStore.submitNationalProcurementReference
            : localStore.submitReference
          // disabled:
          //   !form.canSubmit ||
          //   localStore.checkStatus(ConsultantQualityReferenceStatus.Approved).disabled
        }
      ];
    }

    if (data && data.AvailableStatuses) {
      actionButtons = [
        {
          label: locale.qualityStatuses.RequestSent,
          value: "RequestSent",
          onClick: localStore.submitRequestSent
        },
        {
          label: locale.qualityStatuses.SendRequestIVO,
          value: "RequestSentIVO",
          disabled: localStore.data.AvailableStatuses.find(
            (x) => x.Name === "RequestSentIVO"
          )?.Disabled,
          tooltipTitle: localStore.data.AvailableStatuses.find(
            (x) => x.Name === "RequestSentIVO"
          )?.DisabledMsg,
          onClick: localStore.submitSendRequestIVO
        },
        {
          label: locale.qualityStatuses.RequestReceivedNotProcessed,
          value: "RequestReceivedNotProcessed",
          onClick: localStore.submitRequestReceivedNotProcessed
        },
        {
          label: locale.qualityStatuses.Booked,
          value: "Booked",
          onClick: localStore.submitBooked
        },
        {
          label: locale.qualityStatuses.Ordered,
          value: "Ordered",
          onClick: localStore.submitOrdered
        },
        {
          label: locale.qualityStatuses.ConsultantResponsible,
          value: "ConsultantResponsible",
          onClick: localStore.submitConsultantResponsible
        },
        {
          label: locale.qualityStatuses.ApprovedWithComment,
          value: "ApprovedWithComment",
          onClick: localStore.submitApprovedWithComment
        },
        {
          variant: "contained" as const,
          label: locale.qualityStatuses.Approved,
          value: "Approved",
          onClick: () => {
            if (localStore.data.HasDocument) {
              setAlertOpen(true);
            } else {
              localStore.submitApproved();
            }
          }
        }
      ]
        .filter((x) => localStore.availableStatusNames.includes(x.value))
        .map((option) => ({
          ...option,
          disabled:
            !form.valid ||
            option.disabled ||
            localStore.checkStatus(option.value).disabled
        }));
    }

    if (loading && !data) {
      return (
        <div className={classes.root}>
          <Spinner />
        </div>
      );
    }

    if (localStore.error) {
      return (
        <ErrorCard
          message={localStore.error.toString()}
          title={`Cannot load quality item`}
        />
      );
    }

    const typeOptions = (props.isNationalReference ? npOptions : regularOptions).map(
      (x) => ({
        value: x,
        label: locale.consultantReferenceRelation[x]
      })
    );

    const typeFormValue = form.values.type;

    const typeValue = typeFormValue
      ? {
          value: typeFormValue,
          label: typeOptions.find((x) => x.value === typeFormValue)?.label
        }
      : null;

    const referenceIdFormValue = form.values.referenceId;

    const referenceIdValue = referenceIdFormValue
      ? {
          value: referenceIdFormValue,
          label: referenceOpt.find((x) => x.value === referenceIdFormValue)?.label
        }
      : null;

    return (
      <div className={classes.root} data-test="QualityCheckEditor">
        <SpinnerOverlay
          show={loading}
          style={{ position: "absolute", width: "100%", height: "100%" }}
        />

        <div className={classes.wrapper}>
          <Typography variant="h3" display="block">
            {localStore.name}
          </Typography>

          <div className={classes.form}>
            <Form store={localStore.form} allowUpdates>
              <FormxDebugger store={form} />

              {!localStore.referenceId && form.values.selectedStatus && (
                <div>
                  <StatusMeta store={localStore} />
                </div>
              )}
              <DatePicker
                error={!!form.errors.date}
                value={localStore.form.values.date}
                onChange={(value) => form.setValue("date", value)}
                label={
                  props.referenceId && props.isNationalReference
                    ? locale.StartDateWhenDueShouldCount
                    : locale.Date
                }
                DatePickerProps={{
                  name: "date"
                }}
              />
              {!!props.referenceId && (
                <>
                  <SingleSelect
                    name={`type`}
                    inputLabel={locale.WorkRelationship}
                    styleProps={{
                      control: { showBorder: true },
                      menu: { showBorder: true },
                      menuPortal: { zIndex: 9999 }
                    }}
                    placeholder=""
                    isClearable
                    options={typeOptions}
                    value={typeValue}
                    error={!!form.errors.type}
                    onClear={() => form.setValue("type", null)}
                    onChange={(value) => form.setValue("type", value?.value)}
                    menuPortalTarget={document.body}
                  />

                  {referenceOpt.length > 0 && (
                    <SingleSelect
                      styleProps={{
                        control: { showBorder: true },
                        menu: { showBorder: true }
                      }}
                      name={`referenceId`}
                      inputLabel={locale.References}
                      options={referenceOpt as any}
                      placeholder=""
                      isClearable
                      error={!!form.errors.referenceId}
                      value={referenceIdValue}
                      onClear={() => form.setValue("referenceId", null)}
                      onChange={(value) => form.setValue("referenceId", value?.value)}
                    />
                  )}
                </>
              )}
              {localStore.withDocument && (
                <FileUpload
                  onFileRemove={() => {
                    localStore.fileToUpload = null;
                  }}
                  onFileUpload={(file) => {
                    localStore.fileToUpload = file;

                    localStore.handleUpload();
                  }}
                />
              )}
            </Form>
          </div>

          <div className={classes.controls}>
            <Button
              variant="text"
              size="small"
              color="primary"
              onClick={onClose}
              style={{ marginRight: "auto" }}
            >
              {locale.Cancel}
            </Button>

            <Grid container spacing={1} justifyContent="flex-end">
              <Grid item>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={!localStore.canCancel}
                  onClick={localStore.clearStatus}
                >
                  {locale.ClearStatus}
                </Button>
              </Grid>

              {actionButtons.map((option) => (
                <Grid item>
                  <WithTooltip title={option.tooltipTitle} key={option.value}>
                    <Button
                      key={option.value}
                      disabled={option.disabled}
                      variant={option.variant || "outlined"}
                      color="primary"
                      size="small"
                      onClick={option.onClick}
                    >
                      {option.label}
                    </Button>
                  </WithTooltip>
                </Grid>
              ))}
            </Grid>
          </div>
        </div>

        <AlertDialog
          open={alertOpen}
          onOk={() => {
            localStore.submitApproved();
            setAlertOpen(false);
          }}
          onClose={() => setAlertOpen(false)}
          title={`${locale.continue}?`}
          children={locale.consultantCriminalRecordApproveAlert}
        />
      </div>
    );
  });
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    position: "relative",
    minWidth: 700,
    minHeight: 500
  },

  wrapper: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: 36,
    width: "100%"
  },

  spinner: {
    position: "absolute",
    width: "100%",
    height: "100%"
  },

  overlay: {
    borderRadius: theme.shape.borderRadius,
    marginBottom: "0 !important"
  },

  label: {
    textAlign: "center"
  },

  form: {
    ...theme.mixins.childrenMarginBottom(theme.spacing(2)),
    marginTop: 32,
    flex: 1
  },

  controls: {
    ...theme.mixins.childrenMarginRight(theme.spacing(1)),
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    paddingTop: 40,
    borderTop: `1px solid ${theme.palette.divider}`
  }
}));

const regularOptions = [
  ConsultantQualityReferenceType.CurrentBoss,
  ConsultantQualityReferenceType.LatestBoss,
  ConsultantQualityReferenceType.FormerBoss,
  ConsultantQualityReferenceType.CurrentColleague,
  ConsultantQualityReferenceType.FormerColleague
];

const npOptions = [
  ConsultantQualityReferenceType.CurrentBoss,
  ConsultantQualityReferenceType.LatestBoss,
  ConsultantQualityReferenceType.FormerBoss
];
